import Peer from './peer.js';


export default class RTCPeer extends Peer {


  static get config() {
    // const googleStun = {
    //   urls: 'stun:stun.l.google.com:19302'
    // };

    // https://confluence.my.magora.com/pages/viewpage.action?spaceKey=XCA&title=Turn+Server+Xidras
    const xidrasStun = {
      urls: 'stun:193.239.249.152:3478'
    };

    const xidrasTurn = (transport = 'udp') => ({
      urls: `turn:193.239.249.152:3478?transport=${transport}`,
      username: 'turnmkfiutzurndgehhjjjjuuUi',
      credential: 'g64hhfgtrRzvfuuiiiDvbNbj7645533swwErTz'
    });

    return {
      iceServers: [
        // googleStun,
        xidrasStun,
        // Use `udp` & `tcp` for turn servers
        // https://www.html5rocks.com/en/tutorials/webrtc/infrastructure/#after-signaling-using-ice-to-cope-with-nats-and-firewalls
        xidrasTurn('udp'),
        xidrasTurn('tcp')
      ]
    };
  }


  constructor(serverConnection, peerId) {
    super(serverConnection, peerId);

    this._conn = null;
    this._channel = null;
    this._isCaller = null;
    this._watchCandidateTypesTimeout = null;

    if (peerId) {
      this._connect(peerId, true);
    }
  }


  get isConnected() {
    return this._channel && this._channel.readyState === 'open';
  }


  get isConnecting() {
    return this._channel && this._channel.readyState === 'connecting';
  }


  remove(...args) {
    if (this._conn) {
      this._conn.close();
    }
    super.remove(...args);
  }


  _connect(peerId, isCaller) {
    if (!this._conn) {
      this._openConnection(peerId, isCaller);
    }


    if (isCaller) {
      this._openChannel();
    } else {
      this._conn.ondatachannel = (e) => this._onChannelOpened(e);
    }
  }


  _openConnection(peerId, isCaller) {
    this.id = peerId;
    this._isCaller = isCaller;
    this._conn = new RTCPeerConnection(RTCPeer.config);
    this._conn.onicecandidate = (e) => this._onIceCandidate(e);
    this._conn.onconnectionstatechange = (e) => this._onConnectionStateChange(e);
    this._conn.oniceconnectionstatechange = (e) => this._onIceConnectionStateChange(e);
  }


  _openChannel() {
    const channel = this._conn.createDataChannel('data-channel', {reliable: true});
    channel.binaryType = 'arraybuffer';
    channel.onopen = (e) => this._onChannelOpened(e);
    this._conn.createOffer()
        .then((description) => this._onDescription(description))
        .catch((error) => this._consoleError(error));
  }


  _onDescription(description) {
    // Speed up WebRTC for elderly chrome browsers.
    // https://deepstream.io/tutorials/webrtc/webrtc-file-transfer/#establishing-a-manipulated-p2p-connection
    description.sdp = description.sdp.replace('b=AS:30', 'b=AS:1638400');

    this._conn.setLocalDescription(description)
        .then(() => this._sendSignal({sdp: description}))
        .catch((error) => this._consoleError(error));
  }


  _onIceCandidate(event) {
    if (event.candidate) {
      this._sendSignal({ice: event.candidate});
    }
  }


  onServerMessage(message) {
    if (!this._conn) {
      this._connect(message.sender, false);
    }

    if (message.sdp) {
      this._conn.setRemoteDescription(new RTCSessionDescription(message.sdp))
          .then(() => {
            if (message.sdp.type === 'offer') {
              return this._conn.createAnswer()
                  .then((d) => this._onDescription(d));
            }
          })
          .catch((error) => this._consoleError(error));
    } else if (message.ice) {
      this._conn.addIceCandidate(new RTCIceCandidate(message.ice));
    }
  }


  _onChannelOpened(event) {
    this._consoleLog('channel: opened');
    const channel = event.channel || event.target;
    channel.onmessage = (e) => this._onMessage(e.data);
    channel.onclose = () => this._onChannelClosed();
    this._channel = channel;
  }


  _onChannelClosed() {
    this._consoleLog('channel: closed');
    if (this.isCaller) {
      this._connect(this.id, true); // reopen the channel
    }
  }


  _onConnectionStateChange() {
    const connectionState = this._conn.connectionState;
    this._updatePeer({connectionState});

    this._consoleLog('connectionState:', connectionState);
    switch (connectionState) {
      case 'disconnected':
        this._onChannelClosed();
        break;
      case 'failed':
        this._conn = null;
        this._onChannelClosed();
        break;
    }
  }


  _onIceConnectionStateChange() {
    if (!this._conn) {
      return;
    }

    const iceConnectionState = this._conn.iceConnectionState;
    this._updatePeer({iceConnectionState});

    switch (iceConnectionState) {
      case 'failed':
        this._consoleError({iceConnectionState});
        break;

      case 'connected':
        this._watchCandidateTypes();
        // eslint-disable-line no-fallthrough

      case 'closed':
        this._unwatchCandidateTypes();
        // eslint-disable-line no-fallthrough

      default:
        this._consoleLog('iceConnectionState:', iceConnectionState);
    }
  }


  _send(message) {
    if (!this._channel) {
      return this.refresh();
    }
    this._channel.send(message);
  }


  _sendSignal(signal) {
    this._server.socket.emit('signal', {...signal, to: this.id});
  }


  refresh() {
    // check if channel is open. otherwise create one
    if (!this.isConnected && !this.isConnecting) {
      this._connect(this.id, this._isCaller);
    }
  }


  async _watchCandidateTypes() {
    this._unwatchCandidateTypes();
    if (!this._conn || this._conn.iceConnectionState === 'closed') {
      return;
    }

    // https://stackoverflow.com/a/61571171
    let candidateTypes = {local: null, remote: null};
    const stats = await this._conn.getStats();
    for (const report of stats.values()) {
      if (report.type === 'candidate-pair' && report.nominated) {
        if (report.localCandidateId) {
          candidateTypes.local = stats.get(report.localCandidateId).candidateType;
        }
        if (report.remoteCandidateId) {
          candidateTypes.remote = stats.get(report.remoteCandidateId).candidateType;
        }
        break;
      }
    }

    const {peer} = this;
    if (peer && JSON.stringify(peer.candidateTypes) !== JSON.stringify(candidateTypes)) {
      this._consoleLog('candidateTypes:', candidateTypes);
      this._updatePeer({candidateTypes});
    }

    this._watchCandidateTypesTimeout = setTimeout(() => this._watchCandidateTypes(), 2000);
  }


  _unwatchCandidateTypes() {
    clearTimeout(this._watchCandidateTypesTimeout);
  }
}
